import { useEffect, useState } from 'react'
import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { lessonsApi } from '@/api/lessons'
import type { LessonPriceCalculation } from '@/types'
import { getErrorMessage } from '@/utils/error'
import { formatMoney, formatPriceSource } from './formatMoney'

type Props = {
  recipientType: 'user' | 'group'
  recipientId: number | ''
  plannedStartAt: string
  plannedEndAt: string
}

export function LessonPricePreview({
  recipientType,
  recipientId,
  plannedStartAt,
  plannedEndAt,
}: Props) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<LessonPriceCalculation | null>(null)

  useEffect(() => {
    if (!recipientId || !plannedStartAt || !plannedEndAt) {
      setPreview(null)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    lessonsApi
      .calculatePrice({
        recipient_type: recipientType,
        recipient_id: Number(recipientId),
        planned_start_at: plannedStartAt,
        planned_end_at: plannedEndAt,
      })
      .then((data) => {
        if (!cancelled) setPreview(data)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(getErrorMessage(e) || t('common.error'))
          setPreview(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [recipientType, recipientId, plannedStartAt, plannedEndAt, t])

  if (!recipientId || !plannedStartAt || !plannedEndAt) return null

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {t('billing.lessonPricePreview', 'Предварительная цена урока')}
      </Typography>
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            {t('common.loading', 'Загрузка…')}
          </Typography>
        </Box>
      )}
      {error && (
        <Alert severity="warning" sx={{ py: 0 }}>
          {error}
        </Alert>
      )}
      {!loading && preview && (
        <Typography variant="body2">
          {preview.source === 'subscription' && preview.source_meta ? (
            preview.source_meta.covered_count != null && preview.source_meta.participants_total != null ? (
              t('billing.groupSubscriptionLessonNumber', '{{number}} из {{total}} занятие · {{covered}} из {{participants}} участников по абонементу', {
                number: preview.source_meta.next_number,
                total: preview.source_meta.lessons_total,
                covered: preview.source_meta.covered_count,
                participants: preview.source_meta.participants_total,
              })
            ) : (
              t('billing.subscriptionLessonNumber', '{{number}} из {{total}} занятие по абонементу', {
                number: preview.source_meta.next_number,
                total: preview.source_meta.lessons_total,
              })
            )
          ) : (
            <>
              {preview.price_amount == null
                ? t('billing.freeLesson', 'Бесплатно')
                : formatMoney(preview.price_amount, preview.currency)}{' '}
              · {formatPriceSource(preview.source, t)}
            </>
          )}
        </Typography>
      )}
    </Box>
  )
}
